import { useEffect, useMemo, useState } from "react";
import { Truck, Download, MapPin, ArrowRight, Package, CheckCircle2, Mail, Copy, Radio, Zap, ShieldCheck, Wallet, Clock, Send, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api, { BACKEND_URL, formatApiError } from "@/lib/api";

const SUPPORT_EMAIL = "support@fgbdispatching.com";
const HERO_IMG = "https://images.unsplash.com/photo-1695222833131-54ee679ae8e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwc3Vuc2V0JTIwbG9naXN0aWNzJTIwZnJlaWdodHxlbnwwfHx8fDE3ODg1NDE0NjR8MA&ixlib=rb-4.1.0&q=85";
const FLEET_IMG = "https://images.unsplash.com/photo-1780172168745-2dac9174eb88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxjb21tZXJjaWFsJTIwY2FyZ28lMjB0cnVjayUyMGZsZWV0JTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3ODg1NDE0NjR8MA&ixlib=rb-4.1.0&q=85";

const EQUIPMENT_OPTIONS = ["All Equipment", "Dry Van", "Reefer", "Flatbed", "Step Deck", "Power Only", "Box Truck"];
const STATUS_OPTIONS = [
  { v: "all", label: "All Statuses" },
  { v: "available", label: "Available" },
  { v: "urgent", label: "Urgent / Hot" },
  { v: "covered", label: "Covered" },
];

function StatusBadge({ status }) {
  const map = {
    available: { color: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10", label: "Available", dot: "text-emerald-400" },
    urgent: { color: "text-red-400 border-red-500/30 bg-red-500/10", label: "Hot / Urgent", dot: "text-red-400" },
    covered: { color: "text-slate-400 border-slate-600/40 bg-slate-500/10", label: "Covered", dot: "text-slate-400" },
    cancelled: { color: "text-slate-500 border-slate-700 bg-slate-800/40", label: "Cancelled", dot: "text-slate-500" },
  };
  const s = map[status] || map.available;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-widest ${s.color}`}>
      <span className={`relative inline-flex h-2 w-2 rounded-full ${s.dot} pulse-dot`}>
        <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
      </span>
      {s.label}
    </span>
  );
}

function LoadCard({ load }) {
  const rpm = load.distance_mi && load.distance_mi > 0 ? (load.rate_usd / load.distance_mi).toFixed(2) : null;
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Load Inquiry: ${load.origin_city}, ${load.origin_state} → ${load.dest_city}, ${load.dest_state}`)}&body=${encodeURIComponent(`Load ID: ${load.id}\nEquipment: ${load.equipment}\nRate: $${load.rate_usd}\nPickup: ${load.pickup_date}\n\nI'd like to book this load. My MC# is:`)}`;

  return (
    <div
      data-testid={`load-card-${load.id}`}
      className="group relative rounded-xl border border-slate-800 bg-[#131C31] hover:bg-[#1A2642] transition-colors p-5 flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
            <MapPin className="h-3 w-3 text-amber-400" /> LANE
          </div>
          <div className="mt-1 font-display font-bold text-lg text-white flex items-center gap-2 flex-wrap">
            <span>{load.origin_city}, {load.origin_state}</span>
            <ArrowRight className="h-4 w-4 text-amber-400" />
            <span>{load.dest_city}, {load.dest_state}</span>
          </div>
          {load.distance_mi ? (
            <div className="text-xs text-slate-500 font-mono mt-1">{load.distance_mi} mi</div>
          ) : null}
        </div>
        <StatusBadge status={load.status} />
      </div>

      <div className="flex items-end justify-between border-t border-slate-800 pt-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">All-in Rate</div>
          <div className="font-mono font-bold text-3xl text-amber-400 text-shadow-amber">
            ${Number(load.rate_usd).toLocaleString()}
          </div>
          {rpm ? <div className="text-xs text-slate-500 font-mono">${rpm} / mi</div> : null}
        </div>
        <div className="text-right space-y-1 text-xs">
          <div><span className="text-slate-500">Equipment:</span> <span className="text-slate-200 font-medium">{load.equipment}</span></div>
          <div><span className="text-slate-500">Weight:</span> <span className="text-slate-200 font-mono">{Number(load.weight_lbs).toLocaleString()} lbs</span></div>
          <div><span className="text-slate-500">Pickup:</span> <span className="text-slate-200 font-mono">{load.pickup_date}</span></div>
        </div>
      </div>

      {load.commodity ? (
        <div className="text-xs text-slate-400 border-t border-slate-800 pt-3">
          <span className="text-slate-500 font-mono uppercase tracking-widest text-[10px]">Commodity · </span>{load.commodity}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 pt-1">
        <a href={mailto} className="flex-1 min-w-[180px]">
          <Button
            data-testid={`load-book-btn-${load.id}`}
            className="w-full bg-amber-500 text-black hover:bg-amber-400 font-semibold"
          >
            <Mail className="h-4 w-4 mr-1.5" /> Book / Inquire
          </Button>
        </a>
        <a href={mailto} className="flex-1 min-w-[180px]">
          <Button
            variant="outline"
            data-testid={`load-email-lock-btn-${load.id}`}
            className="w-full border-slate-700 text-slate-200 bg-transparent hover:bg-slate-800 hover:text-white"
          >
            <Send className="h-4 w-4 mr-1.5" /> Email Rate Lock
          </Button>
        </a>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [loads, setLoads] = useState([]);
  const [search, setSearch] = useState("");
  const [equipment, setEquipment] = useState("All Equipment");
  const [statusFilter, setStatusFilter] = useState("all");
  const [packet, setPacket] = useState({ available: false });
  const [form, setForm] = useState({
    company_name: "", mc_dot_number: "", contact_name: "", phone: "", email: "",
    equipment_types: "", number_of_trucks: "", preferred_lanes: "", notes: "",
  });
  const [docFiles, setDocFiles] = useState({ w9: null, coi: null, authority: null });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const [l, p, ph] = await Promise.all([api.get("/loads"), api.get("/packet/info"), api.get("/photos")]);
      setLoads(l.data);
      setPacket(p.data);
      setPhotos(ph.data);
    } catch (e) {
      // silent on public page
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = useMemo(() => {
    return loads.filter((l) => {
      if (equipment !== "All Equipment" && l.equipment !== equipment) return false;
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${l.origin_city} ${l.origin_state} ${l.dest_city} ${l.dest_state} ${l.commodity || ""} ${l.equipment}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [loads, search, equipment, statusFilter]);

  const downloadPacket = () => {
    if (!packet.available) {
      toast.info("The carrier packet has not been uploaded yet. Contact support@fgbdispatching.com.");
      return;
    }
    window.open(`${BACKEND_URL}/api/packet/download`, "_blank");
  };

  const copyEmail = async () => {
    await navigator.clipboard.writeText(SUPPORT_EMAIL);
    toast.success("Email copied to clipboard");
  };

  const submitCarrier = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/carriers", form);
      const carrierId = r.data.id;
      for (const [kind, file] of Object.entries(docFiles)) {
        if (!file) continue;
        try {
          const fd = new FormData();
          fd.append("file", file);
          await api.post(`/carriers/${carrierId}/docs?doc_kind=${kind}`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (docErr) {
          toast.warning(`${file.name} could not be attached — email it to ${SUPPORT_EMAIL}`);
        }
      }
      toast.success("Carrier profile submitted. Our dispatch desk will reach out shortly.");
      setForm({ company_name: "", mc_dot_number: "", contact_name: "", phone: "", email: "", equipment_types: "", number_of_trucks: "", preferred_lanes: "", notes: "" });
      setDocFiles({ w9: null, coi: null, authority: null });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const heroStats = [
    { label: "Avg RPM", value: "$3.45", sub: "top-tier national avg" },
    { label: "Quick Setup", value: "<15 min", sub: "instant packet" },
    { label: "Support", value: "24/7", sub: "dedicated dispatcher" },
    { label: "Returns", value: SUPPORT_EMAIL, sub: "packet · BOL · POD", mono: true },
  ];

  return (
    <div id="top" className="bg-[#080C14] text-slate-200 min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-28 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-radial-amber pointer-events-none" />
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-15 pointer-events-none"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#080C14]/60 to-[#080C14] pointer-events-none" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Badge className="mb-6 border border-amber-500/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/15 font-mono uppercase tracking-[0.2em]">
                <Radio className="h-3 w-3 mr-1.5" /> Live Dispatch · Nationwide
              </Badge>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white">
                High-Yield Freight <span className="text-amber-400">Dispatching</span><br/>
                With Zero Hassle.
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-slate-300 max-w-2xl leading-relaxed">
                We keep your trucks moving at top market RPM. Dedicated dispatchers, transparent contracts,
                instant packet downloads, and 24/7 support — all under one roof.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#load-board">
                  <Button data-testid="hero-browse-loads-btn" size="lg" className="bg-amber-500 text-black hover:bg-amber-400 font-semibold">
                    <Zap className="h-4 w-4 mr-1.5" /> Browse Live Loads
                  </Button>
                </a>
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="hero-download-packet-btn"
                  onClick={downloadPacket}
                  className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 bg-transparent"
                >
                  <Download className="h-4 w-4 mr-1.5" /> Download Carrier Packet
                </Button>
                <a href="#carrier-signup">
                  <Button size="lg" variant="outline" data-testid="hero-join-network-btn" className="border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white bg-transparent">
                    <Truck className="h-4 w-4 mr-1.5" /> Join Carrier Network
                  </Button>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl bg-[#0E1526]/85 backdrop-blur-xl border border-slate-800 p-6 shadow-xl shadow-black/40">
                <div className="flex items-center gap-2 mb-4">
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full text-emerald-400 pulse-dot">
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  </span>
                  <span className="text-xs font-mono uppercase tracking-[0.25em] text-emerald-400">Ops · Live Telemetry</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {heroStats.map((s) => (
                    <div key={s.label} className="rounded-lg bg-[#131C31] border border-slate-800 p-4">
                      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">{s.label}</div>
                      <div className={`mt-1.5 ${s.mono ? "font-mono text-sm break-all text-amber-400" : "font-display font-bold text-2xl text-white"}`}>
                        {s.value}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1">{s.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOAD BOARD */}
      <section id="load-board" className="py-20 border-t border-slate-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400 mb-2">01 · Live Board</div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-white">
                Live Dispatch Offers & Available Freight
              </h2>
              <p className="mt-2 text-slate-400 max-w-2xl">
                Real-time freight offers posted directly by FGB dispatch operations. Filter by equipment, search
                lanes, and inquire instantly via {SUPPORT_EMAIL}.
              </p>
            </div>
            <Badge data-testid="loads-count-badge" className="border border-slate-700 bg-[#131C31] text-slate-300 font-mono">
              {filtered.length} / {loads.length} loads
            </Badge>
          </div>

          <div className="grid md:grid-cols-4 gap-3 mb-8">
            <Input
              data-testid="load-search-input"
              placeholder="Search origin, destination, commodity…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="md:col-span-2 bg-[#131C31] border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-amber-500"
            />
            <Select value={equipment} onValueChange={setEquipment}>
              <SelectTrigger data-testid="load-equipment-filter" className="bg-[#131C31] border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#131C31] border-slate-700 text-white">
                {EQUIPMENT_OPTIONS.map((e) => (
                  <SelectItem key={e} value={e}>{e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="load-status-filter" className="bg-[#131C31] border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#131C31] border-slate-700 text-white">
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {filtered.length === 0 ? (
            <div data-testid="loads-empty-state" className="rounded-2xl border border-dashed border-slate-800 bg-[#0E1526]/50 p-14 text-center">
              <Truck className="h-10 w-10 text-slate-700 mx-auto mb-3" />
              <div className="font-display font-semibold text-lg text-slate-200">No loads match your filters</div>
              <p className="text-sm text-slate-500 mt-1">
                New freight is posted throughout the day. Email {SUPPORT_EMAIL} for private lanes.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="loads-grid">
              {filtered.map((l) => (<LoadCard key={l.id} load={l} />))}
            </div>
          )}
        </div>
      </section>

      {/* CARRIER PACKET */}
      <section id="carrier-packet" className="py-20 border-t border-slate-900 bg-[#0A1120]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400 mb-2">02 · Onboarding</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-white">
              Official Carrier Packet & Setup Docs
            </h2>
            <p className="mt-3 text-slate-400 max-w-xl leading-relaxed">
              Complete our standardized onboarding packet. Download the authorized PDF, fill it out, then email
              returns directly to <span className="text-amber-400 font-mono">{SUPPORT_EMAIL}</span>.
            </p>
            <ul className="mt-6 space-y-2.5" data-testid="packet-checklist">
              {[
                "FGB Broker–Carrier Dispatch Agreement",
                "W-9 Request & Tax Identification Form",
                "Direct Deposit / QuickPay ACH Authorization",
                "Certificate of Insurance (COI) — $1M Auto / $100k Cargo",
                "Notice of Assignment / Factoring Instructions",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            data-testid="download-carrier-packet-card"
            className="rounded-2xl bg-[#0E1526] border border-slate-800 p-8 shadow-xl shadow-black/40 relative overflow-hidden"
          >
            <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-amber-500/10 blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                  <Package className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <div className="font-display font-bold text-xl text-white">FGB Carrier Packet</div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Official PDF · v2026</div>
                </div>
              </div>

              {packet.available ? (
                <div className="text-xs text-slate-500 font-mono mb-4">
                  {packet.filename} · {(packet.size / 1024).toFixed(1)} KB
                </div>
              ) : (
                <div className="text-xs text-amber-400/80 font-mono mb-4">
                  Packet not yet uploaded — contact {SUPPORT_EMAIL}
                </div>
              )}

              <Button
                onClick={downloadPacket}
                data-testid="download-carrier-packet-pdf-btn"
                className="w-full bg-amber-500 text-black hover:bg-amber-400 font-semibold"
                size="lg"
              >
                <Download className="h-4 w-4 mr-1.5" /> Download Carrier Packet (PDF)
              </Button>

              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Carrier Packet Return")}`}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-amber-400 transition"
              >
                <Mail className="h-3.5 w-3.5" /> Email completed packet to {SUPPORT_EMAIL}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CARRIER SIGNUP */}
      <section id="carrier-signup" className="py-20 border-t border-slate-900">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400 mb-2">03 · Fast Track</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-white">
            Instant Carrier Onboarding
          </h2>
          <p className="mt-3 text-slate-400 max-w-2xl leading-relaxed">
            Provide your carrier credentials to get immediate rate confirmations and direct load dispatching.
          </p>

          <form onSubmit={submitCarrier} className="mt-10 rounded-2xl bg-[#0E1526] border border-slate-800 p-6 sm:p-8 grid sm:grid-cols-2 gap-5">
            {[
              { key: "company_name", label: "Company Legal Name *", tid: "signup-company-name-input", req: true },
              { key: "mc_dot_number", label: "MC / DOT Number *", tid: "signup-mc-dot-input", req: true },
              { key: "contact_name", label: "Primary Contact Name *", tid: "signup-contact-name-input", req: true },
              { key: "phone", label: "Direct Dispatch Phone *", tid: "signup-phone-input", req: true },
              { key: "email", label: "Email Address *", tid: "signup-email-input", req: true, type: "email" },
              { key: "equipment_types", label: "Fleet Equipment (Dry Van, Reefer…) *", tid: "signup-equipment-input", req: true },
              { key: "number_of_trucks", label: "Truck Count / Fleet Size", tid: "signup-trucks-input" },
              { key: "preferred_lanes", label: "Preferred States / Regional Lanes", tid: "signup-lanes-input" },
            ].map((f) => (
              <div key={f.key}>
                <Label htmlFor={f.key} className="text-xs font-mono uppercase tracking-wider text-slate-400">{f.label}</Label>
                <Input
                  id={f.key}
                  type={f.type || "text"}
                  required={f.req}
                  data-testid={f.tid}
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1.5 bg-[#131C31] border-slate-700 text-white focus-visible:ring-amber-500"
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label htmlFor="notes" className="text-xs font-mono uppercase tracking-wider text-slate-400">
                Special Operating Requests / Factoring Company
              </Label>
              <Textarea
                id="notes"
                data-testid="signup-notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5 bg-[#131C31] border-slate-700 text-white focus-visible:ring-amber-500 min-h-[90px]"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">
                Attach Documents (optional — PDF or image)
              </Label>
              <div className="mt-2 grid sm:grid-cols-3 gap-3">
                {[
                  { key: "w9", label: "W-9 Form", tid: "signup-doc-w9-input" },
                  { key: "coi", label: "Certificate of Insurance", tid: "signup-doc-coi-input" },
                  { key: "authority", label: "MC Authority", tid: "signup-doc-authority-input" },
                ].map((d) => (
                  <label
                    key={d.key}
                    className="flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-700 hover:border-amber-500/50 bg-[#131C31] p-4 cursor-pointer transition-colors"
                  >
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      data-testid={d.tid}
                      className="hidden"
                      onChange={(e) => setDocFiles({ ...docFiles, [d.key]: e.target.files?.[0] || null })}
                    />
                    {docFiles[d.key] ? (
                      <>
                        <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-medium max-w-full">
                          <CheckCircle2 className="h-4 w-4 shrink-0" />
                          <span className="truncate">{docFiles[d.key].name}</span>
                          <button
                            type="button"
                            data-testid={`${d.tid}-remove`}
                            onClick={(e) => { e.preventDefault(); setDocFiles({ ...docFiles, [d.key]: null }); }}
                            className="text-slate-400 hover:text-red-400"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="text-[10px] text-slate-500 font-mono">{(docFiles[d.key].size / 1024).toFixed(0)} KB</div>
                      </>
                    ) : (
                      <>
                        <Paperclip className="h-4 w-4 text-slate-500" />
                        <div className="text-xs text-slate-300">{d.label}</div>
                        <div className="text-[10px] text-slate-600 font-mono">Tap to attach</div>
                      </>
                    )}
                  </label>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <Button
                type="submit"
                disabled={submitting}
                data-testid="signup-submit-btn"
                className="w-full bg-amber-500 text-black hover:bg-amber-400 font-semibold"
                size="lg"
              >
                {submitting ? "Submitting…" : "Submit Carrier Profile"}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* CONTACT / RETURNS */}
      <section id="contact-returns" className="py-20 border-t border-slate-900 bg-[#0A1120]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400 mb-2">04 · Direct Line</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-white">
            Contact Dispatch & Send Document Returns
          </h2>
          <p className="mt-3 text-slate-400 max-w-2xl leading-relaxed">
            All completed carrier packets, signed rate confirmations, Bills of Lading (BOL), and proof of delivery
            (POD) must be returned directly to our operations inbox.
          </p>

          <div className="mt-8 rounded-2xl bg-gradient-to-br from-[#0E1526] to-[#131C31] border border-amber-500/25 p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4 shadow-xl shadow-black/40">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                <Mail className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Returns Inbox</div>
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Carrier Packet Return / Load Inquiry")}`}
                  data-testid="returns-email-link"
                  className="font-mono text-xl sm:text-2xl text-amber-400 hover:text-amber-300 break-all"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={copyEmail}
                variant="outline"
                data-testid="copy-email-btn"
                className="border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white bg-transparent"
              >
                <Copy className="h-4 w-4 mr-1.5" /> Copy
              </Button>
              <a href={`mailto:${SUPPORT_EMAIL}`}>
                <Button data-testid="email-support-btn" className="bg-amber-500 text-black hover:bg-amber-400 font-semibold">
                  <Send className="h-4 w-4 mr-1.5" /> Email Support
                </Button>
              </a>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {[
              { icon: Package, title: "Packet Returns", desc: "Attach signed packet, COI, and W-9.", badge: "Fast Tracking" },
              { icon: Clock, title: "24/7 Dispatch Desk", desc: "Live dispatchers for booking & roadside support.", badge: "24/7 Desk" },
              { icon: Wallet, title: "QuickPay & Accounting", desc: "Send invoices & rate cons for 24hr ACH settlement.", badge: "Same / Next Day" },
            ].map((c) => (
              <div key={c.title} className="rounded-xl bg-[#0E1526] border border-slate-800 p-5">
                <c.icon className="h-6 w-6 text-amber-400 mb-3" />
                <div className="font-display font-semibold text-white">{c.title}</div>
                <div className="text-sm text-slate-400 mt-1">{c.desc}</div>
                <div className="mt-3 inline-block text-[10px] font-mono uppercase tracking-widest text-amber-400 border border-amber-500/30 rounded-full px-2 py-0.5">
                  {c.badge}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-20 border-t border-slate-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-10 items-center">
          <div className="rounded-2xl overflow-hidden border border-slate-800 aspect-[4/3] relative">
            <img src={FLEET_IMG} alt="FGB Fleet" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#080C14]/80 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-400">Built for the road</div>
              <div className="font-display font-bold text-white text-lg mt-1">Reliability at every mile.</div>
            </div>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400 mb-2">05 · About FGB</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-white">
              Engineered for Carriers. Built for Reliability.
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              FGB Dispatching was founded by industry veterans to eliminate predatory broker margins and
              endless deadhead miles. We represent owner-operators and small-to-midsize fleets with
              relentless rate negotiation, proactive lane planning, and transparent partnerships.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { icon: Zap, title: "Rate Maximization", desc: "We negotiate directly with high-volume shippers and top brokers to lock in premium rates above national averages." },
                { icon: ShieldCheck, title: "Zero Forced Dispatch", desc: "You retain 100% control over your routes, equipment limits, and home-time schedule." },
                { icon: Wallet, title: "Seamless Admin & Paperwork", desc: "We handle all broker setups, rate confirmations, invoicing, factoring, and detention claims." },
              ].map((p) => (
                <div key={p.title} className="flex gap-4">
                  <div className="h-10 w-10 shrink-0 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                    <p.icon className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-white">{p.title}</div>
                    <div className="text-sm text-slate-400 mt-0.5">{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FLEET GALLERY (admin-uploaded photos) */}
      {photos.length > 0 && (
        <section className="py-20 border-t border-slate-900 bg-[#0A1120]">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400 mb-2">Fleet</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-white">On the Road with FGB</h2>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fleet-gallery">
              {photos.map((p) => (
                <div key={p.id} data-testid={`fleet-photo-${p.id}`} className="rounded-xl overflow-hidden border border-slate-800 aspect-[4/3]">
                  <img src={`${BACKEND_URL}${p.url}`} alt={p.original_filename || "FGB fleet"} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="border-t border-slate-900 py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <Truck className="h-4 w-4 text-amber-400" />
            </div>
            <div className="text-sm text-slate-400">
              © {new Date().getFullYear()} FGB Dispatching · All rights reserved.
            </div>
          </div>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-sm text-slate-400 hover:text-amber-400 font-mono"
          >
            {SUPPORT_EMAIL}
          </a>
        </div>
      </footer>
    </div>
  );
}
